import { PATHS } from "@/constants/pathnames";
import { useModal } from "@/contexts/ModalContext";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { Link, usePathname } from "@/i18n/navigation";
import { getRouteDetails } from "@/utils/routeHelper";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import React, { useEffect, useRef, useState } from "react";
import Modal from "./ui/Modal";

export default function ProductNavigationDropdown() {
  const t = useTranslationDetector();
  const pathname = usePathname();
  const routeDetails = getRouteDetails(pathname);
  const bottomSheet = useModal("product-navigation-dropdown");
  const {
    isHome,
    isFlightTicketLanding,
    isBusTicketLanding,
    isFerryTicketHome,
    isCarRentalLanding,
    isHotelLanding,
  } = routeDetails;

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const navigationItems = [
    {
      href: PATHS.FLIGHT_TICKET.PATH,
      icon: "new-flight",
      label: t("header.flight"),
      isActive: isFlightTicketLanding || isHome,
    },
    {
      href: PATHS.HOTEL.PATH,
      icon: "new-hotel",
      label: t("header.hotel"),
      isActive: isHotelLanding,
    },
    {
      href: PATHS.BUS_TICKET.PATH,
      icon: "new-bus",
      label: t("header.bus"),
      isActive: isBusTicketLanding,
    },
    {
      href: PATHS.CAR_RENTAL.PATH,
      icon: "new-car",
      label: t("header.car"),
      isActive: isCarRentalLanding,
    },
    {
      href: PATHS.FERRY_TICKET.PATH,
      icon: "new-ferry",
      label: t("header.ferry"),
      isActive: isFerryTicketHome,
    },
  ];

  const activeItem =
    navigationItems.find((item) => item.isActive) || navigationItems[0];

  const openBottomSheet = (params) => {
    setIsOpen(true);
    bottomSheet.open({
      isBottomSheet: true,
      contentClassName: "!p-0 bg-background-global h-full",
      title: t("common.menu"),
      hideTitleBorder: true,
      backgroundColor: "bg-background-global",
    });
  };

  const handleItemClick = () => {
    setTimeout(() => {
      bottomSheet.close();
      setIsOpen(false);
    }, 500);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative md:hidden" ref={dropdownRef}>
      {/* Dropdown Trigger - Active Item */}
      <button
        onClick={openBottomSheet}
        className={clsx("flex items-center pl-1")}
      >
        <FontAwesomeIcon
          icon={byPrefixAndName.fak[activeItem.icon]}
          className="text-[22px]"
        />
        <FontAwesomeIcon
          icon={byPrefixAndName.fas["chevron-down"]}
          className={clsx(
            "ml-1 text-xs transition-transform duration-200",
            isOpen && "rotate-180",
          )}
        />
      </button>

      <Modal id="product-navigation-dropdown">
        <div className="p-4">
          <div className="shadow-small flex flex-col gap-2 rounded-lg bg-white p-4">
            {navigationItems.map((item, index) => (
              <React.Fragment key={item.id}>
                <Link
                  href={item.href}
                  onClick={handleItemClick}
                  className="flex items-center justify-between py-3"
                >
                  <span className="flex items-center gap-3">
                    <FontAwesomeIcon
                      icon={byPrefixAndName.fak[item.icon]}
                      className="text-primary text-[22px]"
                    />
                    <span className="text-foreground font-medium">
                      {item.label}
                    </span>
                  </span>
                  <FontAwesomeIcon
                    icon={byPrefixAndName.fal["angle-right"]}
                    className="text-foreground-subtle"
                  />
                </Link>
                {index < navigationItems.length - 1 && (
                  <div className="border-border-weaker border-b"></div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </Modal>
    </div>
  );
}
